import React, { useContext, useState } from 'react';
import { Mail, Cake, Eye, EyeOff } from 'lucide-react';
import { AuthContext } from '@/auth/AuthContext';
import Modal from '@/components/Modal';
import api from '@/auth/api';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';

function ProfileCard() {
  const { user } = useContext(AuthContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [visibility, setVisibility] = useState({
    oldPassword: false,
    password: false,
    password2: false,
  });
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const toggleVisibility = (field) => {
    setVisibility((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const validationSchema = Yup.object().shape({
    oldPassword: Yup.string().required('Old password is required'),
    password: Yup.string()
      .min(6, 'New password must be at least 6 characters')
      .required('New password is required'),
    password2: Yup.string()
      .oneOf([Yup.ref('password')], 'Passwords must match')
      .required('Confirm password is required'),
  });

  const handleSubmit = async (values, { resetForm }) => {
    setLoading(true);
    setMessage('');
    try {
      await api.post('/account/panel/changePassword', values);
      setMessage('Password changed successfully!');
      resetForm();
    } catch (error) {
      setMessage(
        error.response?.data?.errors || 'Failed to change password'
      );
    }
    setLoading(false);
  };

  const modalContent = (
    <Formik
      initialValues={{ oldPassword: '', password: '', password2: '' }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {() => (
        <Form className="space-y-4">
          {['oldPassword', 'password', 'password2'].map((field) => (
            <div key={field} className="relative">
              <Field
                name={field}
                type={visibility[field] ? 'text' : 'password'}
                placeholder={
                  field === 'oldPassword'
                    ? 'Current Password'
                    : field === 'password'
                    ? 'New Password'
                    : 'Confirm New Password'
                }
                className="w-full p-2 border rounded pr-10"
              />
              <div
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 cursor-pointer"
                onClick={() => toggleVisibility(field)}
              >
                {visibility[field] ? <EyeOff size={18} /> : <Eye size={18} />}
              </div>
              <ErrorMessage
                name={field}
                component="div"
                className="text-red-500 text-xs mt-1"
              />
            </div>
          ))}
          {message && (
            <p className="text-sm text-center text-blue-600">{message}</p>
          )}
          <button
            type="submit"
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded"
            disabled={loading}
          >
            {loading ? 'Changing...' : 'Change Password'}
          </button>
        </Form>
      )}
    </Formik>
  );

  return (
    <div className="profileCont pt-24">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden max-w-sm mx-auto z-10">
        <div className="relative h-32 bg-gradient-to-r from-blue-500 to-purple-500">
          <div className="absolute -bottom-16 left-1/2 transform -translate-x-1/2">
            <img
              src={user?.profilePic}
              alt={user?.name}
              className="w-32 h-32 rounded-full border-4 border-white dark:border-gray-800"
            />
          </div>
        </div>
        <div className="pt-16 pb-6 px-6 text-center">
          <h2 className="text-2xl font-semibold text-gray-800 dark:text-white">
            {user?.name}
          </h2>
          <div className="mt-6 flex flex-col space-y-3">
            <div className="flex items-center justify-center text-gray-600 dark:text-gray-300">
              <Mail className="w-4 h-4 mr-2" />
              <span className="text-sm">{user?.email}</span>
            </div>
            <div className="flex items-center justify-center text-gray-600 dark:text-gray-300">
              <Cake className="w-4 h-4 mr-2" />
              <span className="text-sm">{user?.dateOfBirth}</span>
            </div>
            <div className="flex items-center justify-center text-gray-600 dark:text-gray-300">
              <Eye className="w-4 h-4 mr-2" />
              <div className="text-sm">Access Level - {user?.roles}</div>
            </div>
            <button
              className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-4 rounded"
              onClick={() => {
                setMessage('');
                setIsModalOpen(true);
              }}
            >
              Change Password
            </button>
          </div>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setMessage('');
        }}
        title="Change Password"
        content={modalContent}
      />
    </div>
  );
}

export default ProfileCard;
